import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Divider,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Tag,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowCounterClockwise,
  CaretDown,
  CaretRight,
  CircleWavyCheck,
  MagnifyingGlass,
} from 'phosphor-react';
import { toast } from 'sonner';
import { IKanbanCardArquivado, useKanban } from '../../../../hooks/useKanban';
import { ILegalizacao, TipoLegalizacao, useLegalizacao } from '../../../../hooks/useLegalizacao';
import { detalheDoErro } from '../legalizacaoPlaybook';
import { EtapaReadOnly } from './EtapaReadOnly';
import { formatarData, infoDoProcesso } from './LegalizacaoArquivados';

interface LegalizacaoFinalizadosProps {
  projetoId?: number;
  tipo: TipoLegalizacao;
  /** cardId → processo. É o `finalizado_em` do processo que separa finalizado de arquivado. */
  legByCardId?: Map<number, ILegalizacao>;
}

type Finalizado = { card: IKanbanCardArquivado; leg: ILegalizacao };

const soAlfaNum = (v?: string | null) => (v ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * Chip "Finalizados (N)" + a lista, no molde do "Arquivados".
 *
 * O "Finalizar" arquiva o card junto, então a fonte é a MESMA lista de arquivados (mesma
 * query e cache), filtrada pelos processos com `finalizado_em`.
 */
export const LegalizacaoFinalizados: React.FC<LegalizacaoFinalizadosProps> = ({
  projetoId,
  tipo,
  legByCardId,
}) => {
  const queryClient = useQueryClient();
  const { getCardsArquivados } = useKanban();
  const { reabrirLegalizacao } = useLegalizacao();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [abertoId, setAbertoId] = useState<number | null>(null);
  const [busca, setBusca] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['legalizacao-arquivados', projetoId],
    queryFn: () => getCardsArquivados(projetoId as number),
    enabled: !!projetoId,
  });

  const itens: Finalizado[] = useMemo(
    () =>
      (data?.data ?? [])
        .map(card => ({ card, leg: legByCardId?.get(card.id) }))
        .filter((x): x is Finalizado => !!x.leg?.finalizado_em)
        .sort(
          (a, b) =>
            Date.parse(b.leg.finalizado_em as string) - Date.parse(a.leg.finalizado_em as string),
        ),
    [data, legByCardId],
  );

  // Busca por nome e, a partir de 3 caracteres, por CNPJ/NIRE/protocolo — a lista só cresce.
  const termo = busca.trim().toLowerCase();
  const termoAlnum = soAlfaNum(termo);
  const visiveis = termo
    ? itens.filter(
        ({ card, leg }) =>
          card.titulo.toLowerCase().includes(termo) ||
          (termoAlnum.length >= 3 &&
            [leg.cnpj, leg.nire, leg.protocolo_numero, leg.gop, leg.gon].some(v =>
              soAlfaNum(v).includes(termoAlnum),
            )),
      )
    : itens;

  const reabrirMutation = useMutation({
    mutationFn: (legId: number) => reabrirLegalizacao(legId),
    onSuccess: () => {
      toast.success('Processo reaberto — voltou para a última etapa do quadro');
      queryClient.invalidateQueries({ queryKey: ['legalizacao-arquivados'] });
      queryClient.invalidateQueries({ queryKey: ['kanban-board'] });
      queryClient.invalidateQueries({ queryKey: ['legalizacoes'] });
    },
    onError: (erro: unknown) =>
      toast.error(detalheDoErro(erro, 'Não foi possível reabrir o processo.')),
  });

  const total = itens.length;

  return (
    <>
      <Tooltip
        label={
          total === 0 ? 'Nenhum processo finalizado neste quadro' : 'Ver processos finalizados'
        }
        openDelay={400}
      >
        {/* Mesmo tratamento discreto do "Arquivados": leva pra outra tela, não recorta o
            quadro — não pode competir com os filtros. */}
        <Button
          size="sm"
          fontSize="13px"
          variant="ghost"
          onClick={onOpen}
          isDisabled={total === 0}
          leftIcon={<CircleWavyCheck size={14} weight="duotone" />}
          borderRadius="full"
          color="gray.500"
          _hover={{ bg: 'blackAlpha.50', color: 'gray.700' }}
          fontWeight="500"
        >
          Finalizados ({isLoading ? '…' : total})
        </Button>
      </Tooltip>

      <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent borderRadius="xl">
          <ModalHeader display="flex" alignItems="center" gap={2} fontSize="18px" fontWeight="700">
            <CircleWavyCheck size={18} weight="duotone" />
            Processos finalizados
            <Tag size="sm" fontSize="10px" colorScheme="green" borderRadius="full" ml={1}>
              {total}
            </Tag>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody pb={5}>
            <Text fontSize="11px" color="gray.500" mb={3}>
              Saíram do quadro pelo &quot;Finalizar&quot; da última etapa. Reabrir devolve o
              processo para ela.
            </Text>

            {total > 0 && (
              <InputGroup size="sm" mb={3}>
                <InputLeftElement pointerEvents="none">
                  <MagnifyingGlass size={15} color="#94A3B8" />
                </InputLeftElement>
                <Input
                  placeholder="Empresa, CNPJ, NIRE, protocolo..."
                  value={busca}
                  onChange={e => setBusca(e.target.value)}
                  borderRadius="full"
                  fontSize="13px"
                />
              </InputGroup>
            )}

            {isLoading && (
              <Flex justify="center" py={10}>
                <Spinner size="lg" color="purple.500" thickness="3px" />
              </Flex>
            )}

            {!isLoading && visiveis.length === 0 && (
              <Text fontSize="11px" color="gray.400" fontStyle="italic" textAlign="center" py={4}>
                {termo ? 'Nenhum finalizado com esse termo.' : 'Nenhum processo finalizado.'}
              </Text>
            )}

            <Flex direction="column" gap={2}>
              {visiveis.map(({ card, leg }) => {
                const infos = infoDoProcesso(leg);
                return (
                  <Box
                    key={card.id}
                    bg="white"
                    borderWidth="1px"
                    borderColor="gray.100"
                    borderRadius="lg"
                    px={4}
                    py={3}
                  >
                    <Flex align="flex-start" justify="space-between" gap={3}>
                      <Box minW={0} flex={1}>
                        <Text fontSize="13px" fontWeight="700" color="gray.800" noOfLines={1}>
                          {card.titulo}
                        </Text>
                        <Text fontSize="11px" color="gray.400" mt={1}>
                          Finalizado em {formatarData(leg.finalizado_em)}
                          {leg.finalizado_por_nome ? ` · por ${leg.finalizado_por_nome}` : ''}
                        </Text>
                        {infos.length > 0 && (
                          <Flex mt={2} flexWrap="wrap" columnGap={4} rowGap={1}>
                            {infos.map(i => (
                              <Text key={i.rotulo} fontSize="11px" color="gray.500">
                                <Text as="span" color="gray.400">
                                  {i.rotulo}:{' '}
                                </Text>
                                <Text as="span" fontWeight="500" color="gray.700">
                                  {i.valor}
                                </Text>
                              </Text>
                            ))}
                          </Flex>
                        )}
                      </Box>

                      <Flex direction="column" gap={1.5} flexShrink={0} align="stretch">
                        <Button
                          size="sm"
                          fontSize="13px"
                          variant="outline"
                          colorScheme="purple"
                          leftIcon={<ArrowCounterClockwise size={14} weight="bold" />}
                          onClick={() => reabrirMutation.mutate(leg.id)}
                          isLoading={
                            reabrirMutation.isPending && reabrirMutation.variables === leg.id
                          }
                        >
                          Reabrir
                        </Button>
                        <Button
                          size="xs"
                          fontSize="13px"
                          variant="ghost"
                          color="gray.500"
                          leftIcon={
                            abertoId === card.id ? (
                              <CaretDown size={12} weight="bold" />
                            ) : (
                              <CaretRight size={12} weight="bold" />
                            )
                          }
                          onClick={() => setAbertoId(abertoId === card.id ? null : card.id)}
                        >
                          {abertoId === card.id ? 'Ocultar' : 'Detalhes'}
                        </Button>
                      </Flex>
                    </Flex>

                    <Collapse in={abertoId === card.id} animateOpacity>
                      <Divider my={3} />
                      <EtapaReadOnly legalizacao={leg} tipo={tipo} coluna={card.coluna_titulo} />
                    </Collapse>
                  </Box>
                );
              })}
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};
